import {
  ArrowUpRight,
  ChevronRight,
  LayoutDashboard,
  Moon,
  Plus,
  Search,
  ShoppingBag,
  Sun,
  UserRound,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useCart } from '../lib/cart';
import { SearchDropdown } from './SearchDropdown';

const marketplaceLinks = [
  { label: 'Browse', href: '/search' },
  { label: 'Human Made', href: '/human-made' },
  { label: 'Digital Products', href: '/digital-products' },
  { label: 'Services', href: '/services' },
  { label: 'Courses', href: '/courses' },
  { label: 'Communities', href: '/communities' },
];

const welcomeLinks = [
  { label: 'Features', href: '/#features' },
  { label: 'For you', href: '/#for-you' },
  { label: 'Marketplace', href: '/search' },
  { label: 'Pricing', href: '/pricing' },
];

export const NavBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const isWelcomeHome = location.pathname === '/' && !user;
  const navigationLinks = isWelcomeHome ? welcomeLinks : marketplaceLinks;
  const { isDark, toggleTheme } = useTheme();
  const { count: cartCount } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [heroSearchVisible, setHeroSearchVisible] = useState(
    location.pathname === '/',
  );
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (location.pathname !== '/') {
      setHeroSearchVisible(false);
      return;
    }
    const heroSearch = document.getElementById('marketplace-hero-search');
    if (!heroSearch) {
      setHeroSearchVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => setHeroSearchVisible(entry.isIntersecting),
      { threshold: 0.12 },
    );
    observer.observe(heroSearch);
    return () => observer.disconnect();
  }, [location.pathname, user, authLoading]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (location.pathname !== '/' || !location.hash || authLoading || mobileMenuOpen) return;
    const target = document.getElementById(location.hash.slice(1));
    if (!target) return;
    const frame = window.requestAnimationFrame(() => target.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth',
      block: 'start',
    }));
    return () => window.cancelAnimationFrame(frame);
  }, [location.pathname, location.hash, authLoading, mobileMenuOpen]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, []);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const menu = mobileMenuRef.current;
    const focusable = () =>
      Array.from(
        menu?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled])',
        ) || [],
      );
    focusable()[0]?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false);
        return;
      }
      if (event.key !== 'Tab') return;
      const elements = focusable();
      if (!elements.length) return;
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
      menuButtonRef.current?.focus();
    };
  }, [mobileMenuOpen]);

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = searchQuery.trim();
    setSearchOpen(false);
    navigate(query ? `/search?q=${encodeURIComponent(query)}` : '/search');
  };

  const showHeaderSearch = location.pathname !== '/' || !heroSearchVisible;
  const hasHeaderSurface = scrolled || location.pathname !== '/' || !isDark;

  return (
    <header
      className={`ws-legacy-header fixed inset-x-0 top-0 z-[100] w-full transition-all duration-300 ${
        scrolled || mobileMenuOpen
          ? 'border-b border-black/[0.08] bg-white/85 shadow-[0_1px_3px_rgba(0,0,0,0.02)] backdrop-blur-xl dark:border-white/[0.08] dark:bg-[#0c0d10]/85'
          : hasHeaderSurface
            ? 'border-b border-black/[0.06] bg-white/75 backdrop-blur-md dark:border-white/[0.06] dark:bg-[#0c0d10]/75'
            : 'border-b border-black/[0.04] bg-white/50 backdrop-blur-sm dark:border-white/[0.04] dark:bg-black/40'
      }`}
    >
      <div className="mx-auto flex h-14 sm:h-16 max-w-[1360px] items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Brand */}
        <Link
          to="/"
          aria-label="Wersee marketplace home"
          className="group flex shrink-0 items-center gap-2.5 rounded-lg py-1 outline-none transition focus-visible:ring-2 focus-visible:ring-indigo-500"
        >
          <img
            src="/brand/wersee-icon-192.png"
            alt=""
            className="h-7 w-7 rounded-lg object-cover shadow-xs transition-transform duration-200 group-hover:scale-105"
          />
          <span className="text-[17px] font-semibold tracking-[-0.03em] text-neutral-950 dark:text-white">
            Wersee
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav
          aria-label="Marketplace"
          className="ml-6 hidden min-w-0 items-center gap-1 lg:flex"
        >
          {navigationLinks.map((link) => {
            const active =
              location.pathname === link.href ||
              (link.href === '/search' && location.pathname === '/search');
            return (
              <Link
                key={link.href}
                to={link.href}
                aria-current={active ? 'page' : undefined}
                className={`inline-flex items-center rounded-full px-3.5 py-1.5 text-xs font-semibold outline-none transition focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                  active
                    ? 'bg-neutral-950 text-white shadow-xs dark:bg-white dark:text-neutral-950'
                    : 'text-neutral-600 hover:bg-black/[0.04] hover:text-neutral-950 dark:text-white/65 dark:hover:bg-white/[0.08] dark:hover:text-white'
                }`}
              >
                {link.label}
              </Link>
            );
          })}
        </nav>

        {/* Desktop Search & Actions */}
        <div className="ml-auto hidden min-w-0 items-center justify-end gap-2 md:flex">
          {showHeaderSearch && (
            <div
              ref={searchRef}
              className="relative w-full max-w-[210px] xl:max-w-[240px]"
            >
              <form onSubmit={submitSearch} role="search">
                <label htmlFor="header-marketplace-search" className="sr-only">
                  Search the Wersee marketplace
                </label>
                <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400 dark:text-white/35" />
                <input
                  id="header-marketplace-search"
                  type="search"
                  value={searchQuery}
                  onFocus={() => setSearchOpen(true)}
                  onChange={(event) => {
                    setSearchQuery(event.target.value);
                    setSearchOpen(true);
                  }}
                  placeholder="Search..."
                  aria-autocomplete="list"
                  aria-expanded={searchOpen}
                  className="h-8.5 w-full rounded-full border border-black/10 bg-black/[0.03] pl-8.5 pr-3 text-xs text-neutral-950 outline-none placeholder:text-neutral-400 focus:border-indigo-500 focus:bg-white focus:ring-2 focus:ring-indigo-500/10 dark:border-white/10 dark:bg-white/[0.05] dark:text-white dark:placeholder:text-white/30 dark:focus:bg-black/40"
                />
              </form>
              <SearchDropdown
                isOpen={searchOpen}
                query={searchQuery}
                onClose={() => setSearchOpen(false)}
              />
            </div>
          )}

          {!isWelcomeHome && (
            <Link
              to="/sell"
              className="inline-flex h-8.5 items-center rounded-full px-3 text-xs font-semibold text-neutral-700 transition hover:bg-black/[0.04] dark:text-white/70 dark:hover:bg-white/[0.06]"
            >
              Sell
            </Link>
          )}

          {cartCount > 0 && (
            <Link
              to="/checkout"
              aria-label={`Open cart with ${cartCount} ${
                cartCount === 1 ? 'item' : 'items'
              }`}
              className="relative flex h-8.5 w-8.5 items-center justify-center rounded-full text-neutral-600 transition hover:bg-black/[0.04] dark:text-white/60 dark:hover:bg-white/[0.06]"
            >
              <ShoppingBag className="h-4 w-4" />
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-indigo-600 px-1 text-[9px] font-bold text-white">
                {cartCount > 9 ? '9+' : cartCount}
              </span>
            </Link>
          )}

          <button
            type="button"
            onClick={toggleTheme}
            aria-label={isDark ? 'Use light theme' : 'Use dark theme'}
            className="flex h-8.5 w-8.5 items-center justify-center rounded-full text-neutral-600 transition hover:bg-black/[0.04] dark:text-white/60 dark:hover:bg-white/[0.06]"
          >
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>

          {user ? (
            <Link
              to="/workspace/overview"
              className="inline-flex h-8.5 items-center gap-1.5 rounded-full bg-neutral-950 px-3.5 text-xs font-semibold text-white shadow-xs transition hover:bg-neutral-800 active:scale-95 dark:bg-white dark:text-black dark:hover:bg-neutral-200"
            >
              <LayoutDashboard className="h-3.5 w-3.5" />
              Workspace
            </Link>
          ) : (
            <>
              <Link
                to="/signin"
                className="inline-flex h-8.5 items-center rounded-full px-3 text-xs font-semibold text-neutral-700 transition hover:bg-black/[0.04] dark:text-neutral-300 dark:hover:bg-white/[0.06]"
              >
                Sign in
              </Link>
              <Link
                to={isWelcomeHome ? '/auth' : '/create'}
                className="inline-flex h-8.5 items-center gap-1.5 rounded-full bg-neutral-950 px-3.5 text-xs font-semibold text-white shadow-xs transition hover:bg-neutral-800 active:scale-95 dark:bg-white dark:text-black dark:hover:bg-neutral-200"
              >
                {isWelcomeHome ? (
                  <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                {isWelcomeHome ? 'Get started' : 'Create'}
              </Link>
            </>
          )}
        </div>

        {/* Mobile Header Controls */}
        <div className="flex items-center gap-1.5 md:hidden">
          {cartCount > 0 && (
            <Link
              to="/checkout"
              aria-label={`Open cart with ${cartCount} ${
                cartCount === 1 ? 'item' : 'items'
              }`}
              className="relative flex h-9 w-9 items-center justify-center rounded-full text-neutral-700 transition hover:bg-black/[0.04] dark:text-white/70 dark:hover:bg-white/[0.06]"
            >
              <ShoppingBag className="h-4 w-4" />
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-indigo-600 px-1 text-[9px] font-bold text-white">
                {cartCount > 9 ? '9+' : cartCount}
              </span>
            </Link>
          )}

          <button
            type="button"
            onClick={toggleTheme}
            aria-label={isDark ? 'Use light theme' : 'Use dark theme'}
            className="flex h-9 w-9 items-center justify-center rounded-full text-neutral-600 transition hover:bg-black/[0.04] dark:text-white/60 dark:hover:bg-white/[0.06]"
          >
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>

          <button
            ref={menuButtonRef}
            type="button"
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            aria-expanded={mobileMenuOpen}
            aria-controls="marketplace-mobile-menu"
            aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            className="relative flex h-9 w-9 items-center justify-center rounded-full border border-black/10 bg-black/[0.03] text-neutral-900 transition-all hover:bg-black/[0.06] active:scale-95 dark:border-white/10 dark:bg-white/[0.06] dark:text-white dark:hover:bg-white/[0.1]"
          >
            <span className="sr-only">
              {mobileMenuOpen ? 'Close menu' : 'Open menu'}
            </span>
            <div className="relative flex h-4 w-4 items-center justify-center">
              <span
                className={`absolute h-[1.5px] w-4 rounded-full bg-current transition-all duration-300 ease-out ${
                  mobileMenuOpen ? 'rotate-45' : '-translate-y-[4px]'
                }`}
              />
              <span
                className={`absolute h-[1.5px] w-4 rounded-full bg-current transition-all duration-300 ease-out ${
                  mobileMenuOpen ? '-rotate-45' : 'translate-y-[4px]'
                }`}
              />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-[95] md:hidden">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setMobileMenuOpen(false)}
              aria-hidden="true"
              className="absolute inset-0 bg-black/50 backdrop-blur-xs"
            />

            {/* Content Sheet attached directly beneath header */}
            <motion.div
              id="marketplace-mobile-menu"
              ref={mobileMenuRef}
              role="dialog"
              aria-modal="true"
              aria-label="Navigation menu"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
              className="absolute inset-x-0 top-14 sm:top-16 max-h-[calc(100dvh-3.5rem)] sm:max-h-[calc(100dvh-4rem)] overflow-y-auto border-b border-black/[0.08] bg-white/95 px-5 pb-8 pt-4 text-neutral-950 shadow-2xl backdrop-blur-2xl dark:border-white/[0.08] dark:bg-[#0c0d10]/95 dark:text-white"
            >
              <form onSubmit={submitSearch} role="search" className="relative mb-3">
                <label htmlFor="mobile-marketplace-search" className="sr-only">
                  Search the Wersee marketplace
                </label>
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400 dark:text-white/40" />
                <input
                  id="mobile-marketplace-search"
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search marketplace..."
                  className="h-10.5 w-full rounded-xl border border-black/10 bg-neutral-100/70 pl-10 pr-4 text-sm text-neutral-900 outline-none transition placeholder:text-neutral-400 focus:border-indigo-500 focus:bg-white dark:border-white/10 dark:bg-white/[0.06] dark:text-white dark:placeholder:text-white/40 dark:focus:bg-black/60"
                />
              </form>

              <nav
                aria-label="Mobile marketplace navigation"
                className="divide-y divide-black/[0.05] dark:divide-white/[0.05]"
              >
                {navigationLinks.map((link) => {
                  const active =
                    location.pathname === link.href ||
                    (link.href === '/search' && location.pathname === '/search');
                  return (
                    <Link
                      key={link.href}
                      to={link.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`flex min-h-12 items-center justify-between py-3 text-base font-medium tracking-tight transition ${
                        active
                          ? 'font-semibold text-indigo-600 dark:text-indigo-400'
                          : 'text-neutral-900 hover:text-indigo-600 dark:text-neutral-100 dark:hover:text-indigo-400'
                      }`}
                    >
                      <span>{link.label}</span>
                      <ChevronRight className="h-4 w-4 text-neutral-400 dark:text-neutral-600" />
                    </Link>
                  );
                })}
              </nav>

              <div className="mt-5 grid gap-2.5 border-t border-black/[0.08] pt-4 dark:border-white/[0.08]">
                <Link
                  to={isWelcomeHome ? '/auth' : '/create'}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex min-h-11 items-center justify-center gap-2 rounded-full bg-neutral-950 px-4 text-sm font-semibold text-white shadow-xs transition hover:bg-neutral-800 active:scale-98 dark:bg-white dark:text-black dark:hover:bg-neutral-200"
                >
                  {isWelcomeHome ? (
                    <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Plus className="h-4 w-4" aria-hidden="true" />
                  )}
                  {isWelcomeHome ? 'Get started' : 'Create'}
                </Link>

                <Link
                  to="/sell"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex min-h-11 items-center justify-center rounded-full border border-black/10 bg-transparent px-4 text-sm font-semibold text-neutral-800 transition hover:bg-black/[0.03] active:scale-98 dark:border-white/10 dark:text-neutral-200 dark:hover:bg-white/[0.05]"
                >
                  Sell on Wersee
                </Link>

                <Link
                  to={user ? '/workspace/overview' : '/signin'}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex min-h-11 items-center justify-center gap-2 rounded-full border border-black/10 bg-transparent px-4 text-sm font-semibold text-neutral-700 transition hover:bg-black/[0.03] active:scale-98 dark:border-white/10 dark:text-neutral-300 dark:hover:bg-white/[0.05]"
                >
                  {user ? (
                    <>
                      <LayoutDashboard className="h-4 w-4" /> Open workspace
                    </>
                  ) : (
                    <>
                      <UserRound className="h-4 w-4" /> Sign in
                    </>
                  )}
                </Link>
              </div>

              <div className="mt-6 flex items-center justify-between text-xs text-neutral-400 dark:text-neutral-500">
                <span>Wersee · Where the internet works</span>
                <Link
                  to="/support"
                  onClick={() => setMobileMenuOpen(false)}
                  className="hover:underline"
                >
                  Help & Support
                </Link>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </header>
  );
};
